import {
  AdminDocumentType,
  BlankDocumentType,
  CarePlanDocumentType,
  ClearanceRequestDocumentType,
  ConsentDocumentType,
  CoverPageDocumentType,
  DmeDocumentType,
  EmergencySummaryDocumentType,
  FinancialDocumentType,
  IdentificationCardDocumentType,
  ImagingResultsDocumentType,
  IncompleteDocumentType,
  InsuranceCardDocumentType,
  IntakeDocumentType,
  JunkDocumentType,
  LabResultsDocumentType,
  LegalDocumentType,
  MedicalHistoryDocumentType,
  MedicalRecordRequestDocumentType,
  NotificationDocumentType,
  OperationNoteDocumentType,
  OrderDocumentType,
  OtherDocumentType,
  PharmacyRelatedDocumentType,
  PriorAuthorizationDocumentType,
  ProcedureDocumentType,
  ProgressNoteDocumentType,
  ReferralDocumentType,
  ReferralStatusRequestDocumentType,
  ReferrerInformationDocumentType,
  ResponseToVhRequestDocumentType,
  UnknownDocumentType,
  VitalChartDocumentType,
} from "@/src/models/constants/document-types";

export enum ClassificationCategory {
  ADMIN = "admin",
  BLANK = "blank",
  CARE_PLAN = "care_plan",
  CLASSIFICATION = "classification",
  CLEARANCE_REQUEST = "clearance_request",
  CONSENT = "consent",
  COVER_PAGE = "cover_page",
  DME_ORDER = "dme_order",
  FINANCIAL = "financial",
  IDENTIFICATION_CARD = "identification_card",
  IMAGING_RESULTS = "imaging_results",
  INCOMPLETE = "incomplete",
  INSURANCE_CARD = "insurance_card",
  INTAKE = "intake",
  JUNK = "junk",
  LAB_RESULTS = "lab_results",
  LEGAL = "legal",
  MEDICAL_HISTORY = "medical_history",
  MEDICAL_RECORD_REQUEST = "medical_record_request",
  MEDICAL_RECORD_REQUEST_HEDIS = "medical_record_request_hedis",
  NOTIFICATION = "notification",
  OTHER = "other",
  PHARMACY_RELATED = "pharmacy_related",
  PRIOR_AUTHORIZATION = "prior_authorization",
  PROCEDURE = "procedure",
  PROGRESS_NOTE = "progress_note",
  REFERER_INFORMATION = "referrer_information",
  REFERRAL = "referral",
  REFERRAL_STATUS_REQUEST = "referral_status_request",
  UNKNOWN = "unknown",
  ORDER_LAB = "order_lab",
  VITAL_CHART = "vital_chart",
}

export type ClassificationSection = {
  label: string;
  options: string[];
};

// Members in LEGACY_CLASSIFICATIONS (defined after this enum) are legacy: keep them for existing customers, but do NOT add them to new customers' configs.
export enum Classification {
  ADMIN = AdminDocumentType.ADMIN,
  ADMIN_ACO = AdminDocumentType.ACO,
  ADMIN_APCM = AdminDocumentType.APCM,
  ADMIN_CASE_MANAGER_DOCUMENTS = AdminDocumentType.CASE_MANAGER_DOCUMENTS,
  ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY = AdminDocumentType.CERTIFICATE_OF_MEDICAL_NECESSITY,
  ADMIN_DEATH_CERTIFICATES = AdminDocumentType.DEATH_CERTIFICATES,
  ADMIN_DISABILITY_FORMS = AdminDocumentType.DISABILITY_FORMS,
  ADMIN_DOH_FORMS = AdminDocumentType.DOH_FORMS,
  ADMIN_FMLA_FORMS = AdminDocumentType.FMLA_FORMS,
  ADMIN_HEAD_START_PROGRAM = AdminDocumentType.HEAD_START_PROGRAM,
  ADMIN_PATIENT_SHARED_INFORMATION = AdminDocumentType.PATIENT_SHARED_INFORMATION,
  ADMIN_PFML_FORMS = AdminDocumentType.PFML_FORMS,
  ADMIN_RESPONSE_FROM_OUTSIDE_PROVIDER_RECORD_NOT_FOUND = AdminDocumentType.RESPONSE_FROM_OUTSIDE_PROVIDER_RECORD_NOT_FOUND,
  ADMIN_RETURN_TO_WORK = AdminDocumentType.RETURN_TO_WORK,
  ADMIN_SCHOOL_NOTES = AdminDocumentType.SCHOOL_NOTES,
  ADMIN_RISK_ASSESSMENT = AdminDocumentType.RISK_ASSESSMENT,
  ADMIN_WORK_OR_SCHOOL_PHYSICAL_FORMS = AdminDocumentType.WORK_OR_SCHOOL_PHYSICAL_FORMS,
  ADMIN_WORKERS_COMP = AdminDocumentType.WORKERS_COMP,
  ORDER_FROM_PHYSICIAN = AdminDocumentType.PHYSICIAN_ORDER,
  ADMIN_PATIENT_QUESTIONNAIRE = AdminDocumentType.PATIENT_QUESTIONNAIRE,
  ADMIN_PATIENT_QUESTIONNAIRE_VANDERBILT = AdminDocumentType.PATIENT_QUESTIONNAIRE_VANDERBILT,
  ADMIN_TRANSITION_CARE = AdminDocumentType.ADMIN_TRANSITION_CARE,
  ADMIN_LEAD_FORM = AdminDocumentType.LEAD_FORM,
  ADMIN_OTHER = AdminDocumentType.OTHER,
  ADMIN_PATIENT_AUTHORIZATION = AdminDocumentType.PATIENT_AUTHORIZATION,
  ADMIN_POLST = AdminDocumentType.POLST,
  ADMIN_SOCIAL_DETERMINANTS = AdminDocumentType.SOCIAL_DETERMINANTS,
  ADMIN_TRANSPORT_FORM = AdminDocumentType.TRANSPORT_FORM,
  BLANK = BlankDocumentType.BLANK,
  CARE_PLAN = CarePlanDocumentType.CARE_PLAN,
  CARE_PLAN_REMINDER = CarePlanDocumentType.REMINDER,
  CLEARANCE_REQUEST = ClearanceRequestDocumentType.CLEARANCE_REQUEST,
  CLEARANCE_REQUEST_RESPONSE = ClearanceRequestDocumentType.RESPONSE,
  CLEARANCE_REQUEST_RESPONSE_PRE_OP = ClearanceRequestDocumentType.RESPONSE_PRE_OP,
  CLEARANCE_REQUEST_RESPONSE_OTHER = ClearanceRequestDocumentType.RESPONSE_OTHER,
  CLEARANCE_REQUEST_PRE_OP = ClearanceRequestDocumentType.PRE_OP,
  CLEARANCE_REQUEST_SURGICAL = ClearanceRequestDocumentType.SURGICAL,
  CONSENT = ConsentDocumentType.CONSENT,
  CONTINUITY_OF_CARE = ProgressNoteDocumentType.CONTINUITY_OF_CARE,
  COVER_PAGE = CoverPageDocumentType.COVER_PAGE,
  DME_DIABETIC_SUPPLIES = DmeDocumentType.DIABETIC_SUPPLIES,
  DME_ORDER = DmeDocumentType.DME_ORDER,
  DME_REQUEST = DmeDocumentType.DME_REQUEST,
  DME_RESPONSE = DmeDocumentType.DME_RESPONSE,
  EMERGENCY_SUMMARY = EmergencySummaryDocumentType.EMERGENCY_SUMMARY,
  FINANCIAL = FinancialDocumentType.FINANCIAL,
  FINANCIAL_BILLING_RECORDS = FinancialDocumentType.BILLING_RECORDS,
  FINANCIAL_ELIGIBILITY_OF_BENEFITS = FinancialDocumentType.ELIGIBILITY_OF_BENEFITS,
  FINANCIAL_ELIGIBILITY_OF_BENEFITS_CREDIT_CARD = FinancialDocumentType.ELIGIBILITY_OF_BENEFITS_CREDIT_CARD,
  FINANCIAL_LEGAL = FinancialDocumentType.LEGAL,
  IDENTIFICATION_CARD = IdentificationCardDocumentType.IDENTIFICATION_CARD,
  IMAGING_RESULTS = ImagingResultsDocumentType.IMAGING_RESULTS,
  IMAGING_RESULTS_MULTI = ImagingResultsDocumentType.MULTI,
  IMAGING_RESULTS_BARIUM_SWALLOW = ImagingResultsDocumentType.BARIUM_SWALLOW,
  IMAGING_RESULTS_COLONOSCOPY = ImagingResultsDocumentType.COLONOSCOPY,
  IMAGING_RESULTS_CT = ImagingResultsDocumentType.CT,
  IMAGING_RESULTS_DEXA = ImagingResultsDocumentType.DEXA,
  IMAGING_RESULTS_ECHO = ImagingResultsDocumentType.ECHO,
  IMAGING_RESULTS_EEG = ImagingResultsDocumentType.EEG,
  IMAGING_RESULTS_ENG = ImagingResultsDocumentType.ENG,
  IMAGING_RESULTS_ESOPHAGRAM = ImagingResultsDocumentType.ESOPHAGRAM,
  IMAGING_RESULTS_MAMMOGRAM = ImagingResultsDocumentType.MAMMOGRAM,
  IMAGING_RESULTS_MRI = ImagingResultsDocumentType.MRI,
  IMAGING_RESULTS_NM = ImagingResultsDocumentType.NM,
  IMAGING_RESULTS_OTHER = ImagingResultsDocumentType.OTHER,
  IMAGING_RESULTS_CARDIAC = ImagingResultsDocumentType.OTHER_CARDIAC,
  IMAGING_RESULTS_EKG = ImagingResultsDocumentType.EKG,
  IMAGING_RESULTS_EMG = ImagingResultsDocumentType.EMG,
  IMAGING_RESULTS_HOLTER_MONITOR = ImagingResultsDocumentType.HOLTER_MONITOR,
  IMAGING_RESULTS_PET = ImagingResultsDocumentType.PET,
  IMAGING_RESULTS_PULMONARY_FUNCTION = ImagingResultsDocumentType.PULMONARY_FUNCTION,
  IMAGING_RESULTS_RETINAL = ImagingResultsDocumentType.RETINAL,
  IMAGING_RESULTS_STRESS_TEST = ImagingResultsDocumentType.STRESS_TEST,
  IMAGING_RESULTS_STRESS_TEST_EXERCISE = ImagingResultsDocumentType.STRESS_TEST_EXERCISE,
  IMAGING_RESULTS_STRESS_TEST_NM = ImagingResultsDocumentType.STRESS_TEST_NM,
  IMAGING_RESULTS_ULTRASOUND = ImagingResultsDocumentType.ULTRASOUND,
  IMAGING_RESULTS_X_RAY = ImagingResultsDocumentType.X_RAY,
  IMAGING_RESULTS_NEEDS_CLARIFICATION = ImagingResultsDocumentType.NEEDS_CLARIFICATION,
  IMAGING_RESULTS_NEEDS_SIGNATURE = ImagingResultsDocumentType.NEEDS_SIGNATURE,
  IMAGING_RESULTS_ENDOSCOPY = ImagingResultsDocumentType.ENDOSCOPY,
  IMAGING_RESULTS_MONITOR_NOTIFICATION = ImagingResultsDocumentType.MONITOR_NOTIFICATION,
  IMAGING_RESULTS_CARDIAC_LEG_IMAGING = ImagingResultsDocumentType.CARDIAC_LEG_IMAGING,
  IMAGING_RESULTS_CLINICAL = ImagingResultsDocumentType.CLINICAL,
  INCOMPLETE = IncompleteDocumentType.INCOMPLETE,
  INCOMPLETE_CANNOT_LINK_TO_PATIENT = IncompleteDocumentType.CANNOT_LINK_TO_PATIENT,
  INCOMPLETE_ILLEGIBLE = IncompleteDocumentType.ILLEGIBLE,
  INCOMPLETE_NOT_ALL_PAGES = IncompleteDocumentType.NOT_ALL_PAGES,
  INSURANCE_CARD = InsuranceCardDocumentType.INSURANCE_CARD,
  INSURANCE_COMMUNICATION = InsuranceCardDocumentType.COMMUNICATION,
  INSURANCE_LETTER_OF_ASSURANCE = InsuranceCardDocumentType.LETTER_OF_ASSURANCE,
  INTAKE = IntakeDocumentType.INTAKE,
  INTAKE_DIALYSIS_QUESTIONAIRE = IntakeDocumentType.INTAKE_DIALYSIS_QUESTIONAIRE,
  JUNK = JunkDocumentType.JUNK,
  JUNK_FAILED_FAX = JunkDocumentType.FAILED_FAX,
  LAB_RESULTS = LabResultsDocumentType.LAB_RESULTS,
  LAB_RESULTS_A1C = LabResultsDocumentType.A1C,
  LAB_RESULTS_BILIRUBIN = LabResultsDocumentType.BILIRUBIN,
  LAB_RESULTS_BP = LabResultsDocumentType.BP,
  LAB_RESULTS_CBC = LabResultsDocumentType.CBC,
  LAB_RESULTS_CMP = LabResultsDocumentType.CMP,
  LAB_RESULTS_COLOGUARD = LabResultsDocumentType.COLOGUARD,
  LAB_RESULTS_DRUG_SCREEN = LabResultsDocumentType.DRUG_SCREEN,
  LAB_RESULTS_FIT = LabResultsDocumentType.FIT,
  LAB_RESULTS_HEARING_TEST = LabResultsDocumentType.HEARING_TEST,
  LAB_RESULTS_HEP = LabResultsDocumentType.HEP,
  LAB_RESULTS_LIPID_PANEL = LabResultsDocumentType.LIPID_PANEL,
  LAB_RESULTS_MICROALBUMIN = LabResultsDocumentType.MICROALBUMIN,
  LAB_RESULTS_MULTIPLE_LABS = LabResultsDocumentType.MULTIPLE_LABS,
  LAB_RESULTS_ORDER_FORM = LabResultsDocumentType.ORDER_FORM,
  LAB_RESULTS_ORDER_FORM_NEEDS_SIGNATURE = LabResultsDocumentType.ORDER_FORM_NEEDS_SIGNATURE,
  LAB_RESULTS_OTHER = LabResultsDocumentType.OTHER,
  LAB_RESULTS_PAP_SMEAR = LabResultsDocumentType.PAP_SMEAR,
  LAB_RESULTS_PATHOLOGY = LabResultsDocumentType.PATHOLOGY,
  LAB_RESULTS_PKU = LabResultsDocumentType.PKU,
  LAB_RESULTS_PT_INR = LabResultsDocumentType.PT_INR,
  LAB_RESULTS_QUANTAFLO = LabResultsDocumentType.QUANTAFLO,
  LAB_RESULTS_TB = LabResultsDocumentType.TB,
  LAB_RESULTS_TEST_NOT_PERFORMED = LabResultsDocumentType.TEST_NOT_PERFORMED,
  LAB_RESULTS_URINALYSIS = LabResultsDocumentType.URINALYSIS,
  LEGAL = LegalDocumentType.LEGAL,
  LEGAL_ADVANCED_CARE_DIRECTIVE = LegalDocumentType.ADVANCED_CARE_DIRECTIVE,
  LEGAL_COURT_DOCUMENTS = LegalDocumentType.COURT_DOCUMENTS,
  LEGAL_POWER_OF_ATTORNEY = LegalDocumentType.POWER_OF_ATTORNEY,
  MEDICAL_HISTORY = MedicalHistoryDocumentType.MEDICAL_HISTORY,
  MEDICAL_HISTORY_CHART_FROM_CLINIC = MedicalHistoryDocumentType.CHART_FROM_CLINIC,
  MEDICAL_HISTORY_CHART_FROM_HOSPITAL = MedicalHistoryDocumentType.CHART_FROM_HOSPITAL,
  MEDICAL_HISTORY_COVID = MedicalHistoryDocumentType.COVID,
  MEDICAL_HISTORY_RESPONSE = MedicalHistoryDocumentType.RESPONSE,
  MEDICAL_HISTORY_HOME_HEALTH = MedicalHistoryDocumentType.HOME_HEALTH,
  MEDICAL_HISTORY_HOSPICE = MedicalHistoryDocumentType.HOSPICE,
  MEDICAL_HISTORY_IMMUNIZATION = MedicalHistoryDocumentType.IMMUNIZATION,
  MEDICAL_HISTORY_NUTRITION = MedicalHistoryDocumentType.NUTRITION,
  MEDICAL_HISTORY_MEDICATIONS = MedicalHistoryDocumentType.MEDICAL_HISTORY_MEDICATIONS,
  MEDICAL_RECORD_REQUEST = MedicalRecordRequestDocumentType.MEDICAL_RECORD_REQUEST,
  MEDICAL_RECORD_REQUEST_DOH = MedicalRecordRequestDocumentType.DOH,
  MEDICAL_RECORD_REQUEST_IMAGING_CD = MedicalRecordRequestDocumentType.IMAGING_CD,
  MEDICAL_RECORD_REQUEST_HEDIS = MedicalRecordRequestDocumentType.MEDICAL_RECORD_REQUEST_HEDIS,
  MEDICAL_RECORD_REQUEST_LEGAL = MedicalRecordRequestDocumentType.LEGAL,
  MEDICAL_RECORD_REQUEST_OTHER = MedicalRecordRequestDocumentType.OTHER,
  MEDICAL_RECORD_REQUEST_RESPONSE = MedicalRecordRequestDocumentType.RESPONSE,
  MEDICAL_RECORD_REQUEST_SSA = MedicalRecordRequestDocumentType.SSA,
  NOTIFICATION = NotificationDocumentType.NOTIFICATION,
  NOTIFICATION_HOSPITAL_ADMIT = NotificationDocumentType.HOSPITAL_ADMIT,
  NOTIFICATION_HOSPITAL_DISCHARGE = NotificationDocumentType.HOSPITAL_DISCHARGE,
  NOTIFICATION_HOSPITAL_ER_ADMIT = NotificationDocumentType.HOSPITAL_ER_ADMIT,
  NOTIFICATION_HOSPITAL_ER_DISCHARGE = NotificationDocumentType.HOSPITAL_ER_DISCHARGE,
  NOTIFICATION_HOSPITAL_FACE_SHEET = NotificationDocumentType.HOSPITAL_FACE_SHEET,
  NOTIFICATION_APPOINTMENT_CANCELLED_MISSED = NotificationDocumentType.APPOINTMENT_CANCELLED_MISSED,
  NOTIFICATION_APPOINTMENT = NotificationDocumentType.APPOINTMENT_OTHER,
  NOTIFICATION_HOSPITAL_SDS_ADMIT = NotificationDocumentType.HOSPITAL_SDS_ADMIT,
  NOTIFICATION_HOSPITAL_SDS_DISCHARGE = NotificationDocumentType.HOSPITAL_SDS_DISCHARGE,
  OPERATION_NOTE = OperationNoteDocumentType.OPERATION_NOTE,
  OTHER = OtherDocumentType.OTHER,
  ORDER_CARDIAC = OrderDocumentType.CARDIAC,
  ORDER_LAB = OrderDocumentType.LAB,
  ORDER_CLARIFICATION = OrderDocumentType.CLARIFICATION,
  ORDER_IMAGING = OrderDocumentType.IMAGING,
  ORDER_PROCEDURE = OrderDocumentType.PROCEDURE,
  ORDER_SIGNED = OrderDocumentType.SIGNED,
  PHARMACY_RELATED = PharmacyRelatedDocumentType.PHARMACY_RELATED,
  PHARMACY_RELATED_CARE_CONSIDERATION = PharmacyRelatedDocumentType.CARE_CONSIDERATION,
  PHARMACY_RELATED_CLARIFICATION = PharmacyRelatedDocumentType.CLARIFICATION,
  PHARMACY_RELATED_CONTROLED_SUBSTANCES_POLICY = PharmacyRelatedDocumentType.CONTROLED_SUBSTANCES_POLICY,
  PHARMACY_RELATED_DRUG_MONITORING_PROGRAM = PharmacyRelatedDocumentType.DRUG_MONITORING_PROGRAM,
  PHARMACY_RELATED_MED_RECONCILIATION = PharmacyRelatedDocumentType.MED_RECONCILIATION,
  PHARMACY_RELATED_MEDICATION_LIST = PharmacyRelatedDocumentType.MEDICATION_LIST,
  PHARMACY_RELATED_NON_ADHERENCE = PharmacyRelatedDocumentType.NON_ADHERENCE,
  PHARMACY_RELATED_REFILL = PharmacyRelatedDocumentType.REFILL,
  PHARMACY_RELATED_REFILL_MANY = PharmacyRelatedDocumentType.REFILL_MANY,
  PHARMACY_RELATED_ALERT = PharmacyRelatedDocumentType.THERAPEUTIC_ALERT,
  PRIOR_AUTHORIZATION = PriorAuthorizationDocumentType.PRIOR_AUTHORIZATION,
  PRIOR_AUTHORIZATION_IMAGING_REQUEST = PriorAuthorizationDocumentType.IMAGING_REQUEST,
  PRIOR_AUTHORIZATION_IMAGING_RESPONSE = PriorAuthorizationDocumentType.IMAGING_RESPONSE,
  PRIOR_AUTHORIZATION_MEDICATION_REQUEST = PriorAuthorizationDocumentType.MEDICATION_REQUEST,
  PRIOR_AUTHORIZATION_MEDICATION_RESPONSE = PriorAuthorizationDocumentType.MEDICATION_RESPONSE,
  PRIOR_AUTHORIZATION_PROCEDURE_REQUEST = PriorAuthorizationDocumentType.PROCEDURE_REQUEST,
  PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE = PriorAuthorizationDocumentType.PROCEDURE_RESPONSE,
  PRIOR_AUTHORIZATION_REFERRAL_REQUEST = PriorAuthorizationDocumentType.REFERRAL_REQUEST,
  PRIOR_AUTHORIZATION_REFERRAL_RESPONSE = PriorAuthorizationDocumentType.REFERRAL_RESPONSE,
  PRIOR_AUTHORIZATION_SURGERY_REQUEST = PriorAuthorizationDocumentType.SURGERY_REQUEST,
  PRIOR_AUTHORIZATION_SURGERY_RESPONSE = PriorAuthorizationDocumentType.SURGERY_RESPONSE,
  PRIOR_AUTHORIZATION_THERAPY_REQUEST = PriorAuthorizationDocumentType.THERAPY_REQUEST,
  PRIOR_AUTHORIZATION_THERAPY_RESPONSE = PriorAuthorizationDocumentType.THERAPY_RESPONSE,
  PROCEDURE = ProcedureDocumentType.PROCEDURE,
  PROCEDURE_CARDIAC = ProcedureDocumentType.CARDIAC,
  PROCEDURE_CARDIAC_VEIN = ProcedureDocumentType.CARDIAC_VEIN,
  PROCEDURE_REHAB = ProcedureDocumentType.REHAB,
  PROCEDURE_DEVICE_REQUEST = ProcedureDocumentType.DEVICE_REQUEST,
  PROCEDURE_DEVICE_INFO = ProcedureDocumentType.DEVICE_INFO,
  PROCEDURE_GI = ProcedureDocumentType.GI,
  PROCEDURE_INVENTORY_LIST = ProcedureDocumentType.INVENTORY_LIST,
  PROCEDURE_SLEEP = ProcedureDocumentType.SLEEP,
  PROGRESS_NOTE = ProgressNoteDocumentType.PROGRESS_NOTE,
  PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY = ProgressNoteDocumentType.HOSPITAL_DISCHARGE_SUMMARY,
  PROGRESS_NOTE_ER_REPORT = ProgressNoteDocumentType.ER_REPORT,
  PROGRESS_NOTE_ER_SUMMARY = ProgressNoteDocumentType.ER_SUMMARY,
  PROGRESS_NOTE_HOSPITAL_OFFICE_VISIT = ProgressNoteDocumentType.HOSPITAL_OFFICE_VISIT,
  PROGRESS_NOTE_HOSPITAL_RECORDS = ProgressNoteDocumentType.HOSPITAL_RECORDS,
  PROGRESS_NOTE_HOSPITAL_SUMMARY = ProgressNoteDocumentType.HOSPITAL_SUMMARY,
  PROGRESS_NOTE_OFFICE_VISIT = ProgressNoteDocumentType.OFFICE_VISIT,
  PROGRESS_NOTE_OPERATIVE_REPORT = ProgressNoteDocumentType.OPERATIVE_REPORT,
  PROGRESS_NOTE_PRE_OPERATIVE = ProgressNoteDocumentType.PRE_OPERATIVE,
  PROGRESS_NOTE_PT = ProgressNoteDocumentType.PT,
  PROGRESS_NOTE_URGENT_CARE = ProgressNoteDocumentType.URGENT_CARE,
  PROGRESS_NOTE_VITAL_CHART = ProgressNoteDocumentType.VITAL_CHART,
  PROGRESS_NOTE_FLOW_SHEET = ProgressNoteDocumentType.FLOW_SHEET,
  CONSENT_FOR_RECORDS_SPECIALIST = ReferralDocumentType.CONSENT_FOR_RECORDS_SPECIALIST,
  REFERRAL = ReferralDocumentType.REFERRAL,
  REFERRAL_OUTSIDE = ReferralDocumentType.REFERRAL_OUTSIDE,
  REFERRAL_REQUEST = ReferralDocumentType.REFERRAL_REQUEST,
  REFERRAL_RESPONSE = ReferralDocumentType.REFERRAL_RESPONSE,
  REFERER_INFORMATION = ReferrerInformationDocumentType.REFERRER_INFORMATION,
  REFERRAL_STATUS_REQUEST = ReferralStatusRequestDocumentType.REFERRAL_STATUS_REQUEST,
  RESPONSE_TO_VH_REQUEST = ResponseToVhRequestDocumentType.RESPONSE_TO_VH_REQUEST,
  UNKNOWN = UnknownDocumentType.UNKNOWN,
  VITAL_CHART = VitalChartDocumentType.VITAL_CHART,
}

/**
 * Legacy classifications: keep for existing customers, but don't use for new ones.
 */
export const LEGACY_CLASSIFICATIONS = [
  Classification.ADMIN_ACO,
  Classification.ADMIN_APCM,
  Classification.ADMIN_PFML_FORMS,
  Classification.ADMIN_RESPONSE_FROM_OUTSIDE_PROVIDER_RECORD_NOT_FOUND,
  Classification.ADMIN_SCHOOL_NOTES,
  Classification.ADMIN_WORKERS_COMP,
  Classification.ADMIN_TRANSITION_CARE,
  Classification.ADMIN_OTHER,
  Classification.ADMIN_PATIENT_AUTHORIZATION,
  Classification.CLEARANCE_REQUEST_RESPONSE_PRE_OP,
  Classification.CLEARANCE_REQUEST_RESPONSE_OTHER,
  Classification.CLEARANCE_REQUEST_PRE_OP,
  Classification.CLEARANCE_REQUEST_SURGICAL,
  Classification.DME_DIABETIC_SUPPLIES,
  Classification.FINANCIAL_LEGAL,
  Classification.IMAGING_RESULTS_MULTI,
  Classification.IMAGING_RESULTS_COLONOSCOPY,
  Classification.IMAGING_RESULTS_OTHER,
  Classification.IMAGING_RESULTS_STRESS_TEST_EXERCISE,
  Classification.IMAGING_RESULTS_STRESS_TEST_NM,
  Classification.LAB_RESULTS_A1C,
  Classification.LAB_RESULTS_BILIRUBIN,
  Classification.LAB_RESULTS_CBC,
  Classification.LAB_RESULTS_CMP,
  Classification.LAB_RESULTS_COLOGUARD,
  Classification.LAB_RESULTS_FIT,
  Classification.LAB_RESULTS_HEARING_TEST,
  Classification.LAB_RESULTS_HEP,
  Classification.LAB_RESULTS_LIPID_PANEL,
  Classification.LAB_RESULTS_MICROALBUMIN,
  Classification.LAB_RESULTS_OTHER,
  Classification.LAB_RESULTS_PAP_SMEAR,
  Classification.LAB_RESULTS_PKU,
  Classification.LAB_RESULTS_PT_INR,
  Classification.LAB_RESULTS_TB,
  Classification.LAB_RESULTS_URINALYSIS,
  Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC,
  Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL,
  Classification.MEDICAL_HISTORY_COVID,
  Classification.MEDICAL_HISTORY_HOME_HEALTH,
  Classification.MEDICAL_HISTORY_HOSPICE,
  Classification.MEDICAL_HISTORY_NUTRITION,
  Classification.MEDICAL_HISTORY_MEDICATIONS,
  Classification.MEDICAL_RECORD_REQUEST_DOH,
  Classification.MEDICAL_RECORD_REQUEST_IMAGING_CD,
  Classification.MEDICAL_RECORD_REQUEST_LEGAL,
  Classification.MEDICAL_RECORD_REQUEST_OTHER,
  Classification.MEDICAL_RECORD_REQUEST_SSA,
  Classification.NOTIFICATION_HOSPITAL_ER_ADMIT,
  Classification.NOTIFICATION_HOSPITAL_ER_DISCHARGE,
  Classification.NOTIFICATION_APPOINTMENT_CANCELLED_MISSED,
  Classification.NOTIFICATION_HOSPITAL_SDS_ADMIT,
  Classification.NOTIFICATION_HOSPITAL_SDS_DISCHARGE,
  Classification.ORDER_CARDIAC,
  Classification.ORDER_LAB,
  Classification.ORDER_IMAGING,
  Classification.ORDER_PROCEDURE,
  Classification.PHARMACY_RELATED_CARE_CONSIDERATION,
  Classification.PHARMACY_RELATED_NON_ADHERENCE,
  Classification.PHARMACY_RELATED_REFILL_MANY,
  Classification.PRIOR_AUTHORIZATION_IMAGING_REQUEST,
  Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_SURGERY_REQUEST,
  Classification.PRIOR_AUTHORIZATION_SURGERY_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_THERAPY_REQUEST,
  Classification.PRIOR_AUTHORIZATION_THERAPY_RESPONSE,
  Classification.PROCEDURE_REHAB,
  Classification.PROCEDURE_GI,
  Classification.PROGRESS_NOTE_ER_SUMMARY,
  Classification.PROGRESS_NOTE_HOSPITAL_OFFICE_VISIT,
  Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY,
  Classification.PROGRESS_NOTE_OFFICE_VISIT,
  Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
  Classification.PROGRESS_NOTE_URGENT_CARE,
  Classification.CONSENT_FOR_RECORDS_SPECIALIST,
  Classification.REFERRAL_OUTSIDE,
  Classification.VITAL_CHART,
];

export const ClassificationDescriptions: Record<Classification, string> = {
  [Classification.ADMIN]:
    "General administrative documents, forms, and paperwork that don't fit more specific categories. Use this when no other classification is appropriate.",
  [Classification.ADMIN_CASE_MANAGER_DOCUMENTS]: `
    Documents from case managers, social workers, insurance case management, or CCM (Chronic Care Management) 
    Look for terms like case management, complex care management (CCM), insurance case management, social work or social workers, 
    `,
  [Classification.ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY]: `A Certificate of Medical Necessity (CMN) is a document that verifies the need for specific medical equipment or supplies, 
    often required by insurance providers, housing accomodations, or other accomodation requests. `,
  [Classification.ADMIN_DEATH_CERTIFICATES]: "Official death certificates, death reports, or mortality documentation",
  [Classification.ADMIN_DISABILITY_FORMS]: `Medical disability forms are documents that request the verification or provider assessment of a patients's disability. 
    These are often used to apply for or report on disability benefits, either for the Social Security Administration (SSA) or a State Disability Insurance (SDI) program. They can also relate to types of accomodation requests.`,
  [Classification.ADMIN_DOH_FORMS]:
    "Department of Health (DOH) forms, including illness report forms, communicable disease reports, and other public health reporting documents required by state or local health departments.",
  [Classification.ADMIN_FMLA_FORMS]: `
    The Family and Medical Leave Act (FMLA) is a United States labor law that provides eligible employees with unpaid, job-protected leave for certain family and medical reasons. 
    Look for the terms FMLA, family leave, extended leave, leave request
    `,
  [Classification.ADMIN_HEAD_START_PROGRAM]:
    "Head Start program documentation, early childhood development forms, or Head Start medical requirements",
  [Classification.ADMIN_PATIENT_SHARED_INFORMATION]: `
    Patient Logs from personal medical devices (for example Blood Pressure/Glucose monitor, CGM) or other information shared by a patinet regarding their health or medications
    `,
  [Classification.ADMIN_PFML_FORMS]:
    "Paid Family and Medical Leave (PFML) forms — Washington state specific paid, job-protected leave. Distinct from FMLA. Look for the term PFML or Paid Family and Medical Leave.",
  [Classification.ADMIN_RESPONSE_FROM_OUTSIDE_PROVIDER_RECORD_NOT_FOUND]: `
    Responses from external providers indicating records not found, no records available, or negative responses to record requests.
    When a medical records request is made but is not able to be completed, providers will respond with 'patient records not found' or 'not a patient of ours' or something to that effect. 
    This information is often found in typed or handwritten notes on the cover page or first page of the document.
`,
  [Classification.ADMIN_RETURN_TO_WORK]:
    "Return to work forms, work clearance documents, or fitness for duty evaluations",
  [Classification.ADMIN_RISK_ASSESSMENT]: `
    A medical risk assessment is a process used to identify, evaluate, and manage potential health risks to patients. 
    These are sometimes related to insurance and can have terms like 'risk adjustment', 'health risk assessment (HRA). 
    They can also be from other providers asking for a risk assessment regarding certain procedures or medications`,
  [Classification.ADMIN_SCHOOL_NOTES]:
    "School-related medical notes, school physical forms, or documentation for school requirements. Look for school, student",
  [Classification.ADMIN_WORK_OR_SCHOOL_PHYSICAL_FORMS]:
    "Pre-employment physicals, school physical examinations, or work/school clearance forms",
  [Classification.ADMIN_WORKERS_COMP]:
    "Workers' compensation (Labor & Industries / L&I) documents related to work-related injuries or illnesses. At TriCity Ortho these are typically labeled as L&I documents, representing Washington State Labor & Industries.",
  [Classification.ORDER_FROM_PHYSICIAN]: `
  Look for documents that ask for a procedure request. The procedure can be a laboratory test, imaging test, or treatment. 
  There is usually patient information, an ordering provider, ordering location, test names, and valid dates. 
  There is also sometimes a provider signature and test codes. Look for the words "Order" or "Services Needed".
  `,
  [Classification.ADMIN_PATIENT_QUESTIONNAIRE]:
    "Patient questionnaires, patient intake forms, or patient intake documentation",
  [Classification.ADMIN_PATIENT_QUESTIONNAIRE_VANDERBILT]:
    "Vanderbilt ADHD assessment questionnaires. Look for the word 'Vanderbilt' on the document.",
  [Classification.ADMIN_TRANSITION_CARE]: `Document that records the transfer of a patient’s care responsibility from one healthcare setting or provider to another`,
  [Classification.ADMIN_LEAD_FORM]: `
  Lead form, lead intake form, or lead intake documentation. Look for terms like 'lead', 'lead form'
  `,
  [Classification.ADMIN_ACO]: "ACO (Accountable Care Organization) consent forms and documentation",
  [Classification.ADMIN_APCM]: "APCM (Advance Care Planning and Management) consent forms and documentation",
  [Classification.ADMIN_OTHER]: "Other administrative documents not fitting specific admin categories",
  [Classification.ADMIN_PATIENT_AUTHORIZATION]: "Patient authorization forms for release of information or consent",
  [Classification.ADMIN_POLST]: "POLST (Physician Orders for Life-Sustaining Treatment) forms",
  [Classification.ADMIN_SOCIAL_DETERMINANTS]: "Social determinants of health screening and assessment forms",
  [Classification.ADMIN_TRANSPORT_FORM]:
    "Medical transport forms, transportation eligibility/authorization forms, or non-emergency medical transport (NEMT) paperwork — documents that authorize, verify, or coordinate a patient's medical transportation.",
  [Classification.BLANK]: "Empty pages, blank forms, white space, or pages with no meaningful content or text",
  [Classification.CARE_PLAN]:
    "Comprehensive care plans, treatment plans, care coordination documents, or patient care planning forms. Includes Prescribed Pediatric Extended Care (PPEC) plans of care — set serviceLine to PPEC for those.",
  [Classification.CARE_PLAN_REMINDER]:
    "Reminders for previously sent care plans, including first and second POC (Plan of Care) reminder notices.",
  [Classification.CLEARANCE_REQUEST]: `General medical clearance requests, fitness evaluations, or clearance for activities/procedures.
    A clearance request will state "clearance request", "clearance needed", "cardiovascular clearance" or "clearance order".`,
  [Classification.CLEARANCE_REQUEST_PRE_OP]: `
    This is a clearance request related to an operation or procedure typically asking if a patient is medically stable for a procedure or if they can halt a medication prior to a procedure. 
    Look for pre-op, operation, procedure, clearance, cleared, medically stable 
    `,
  [Classification.CLEARANCE_REQUEST_SURGICAL]:
    "Surgical clearance documentation, pre-surgical evaluations, or surgical fitness assessments",
  [Classification.CLEARANCE_REQUEST_RESPONSE]:
    "a response to a dental clearance request. These documents will have information about the patient being cleared for a procedure/medication and will be coming from a dentist/dental provider.",
  [Classification.CLEARANCE_REQUEST_RESPONSE_PRE_OP]:
    "A response to a pre-operative clearance request, indicating the patient's clearance status for an upcoming surgical procedure.",
  [Classification.CLEARANCE_REQUEST_RESPONSE_OTHER]:
    "A response to a clearance request that does not fit the pre-operative category — general clearance responses for non-surgical procedures.",
  [Classification.CONSENT]: `
    This is a consent document for the release of medical records, or any other document regarding consent
    `,
  [Classification.CONTINUITY_OF_CARE]:
    "Continuity of care documents for handover of patient care between organizations or providers.",
  [Classification.COVER_PAGE]:
    "Document cover pages, title pages, fax cover sheets, or document headers with basic identifying information",
  [Classification.DME_DIABETIC_SUPPLIES]: `
    Durable Medical Equipment (DME) supplies for diabetes include a wide range of items used to manage the condition, such as blood glucose monitors, test strips, lancets, insulin pumps, and continuous glucose monitoring (CGM) systems
    `,
  [Classification.DME_ORDER]:
    "Durable medical equipment orders, medical device prescriptions, or equipment authorization forms: A common term in a document will be 'set up confirmation' or 'equipment order'",
  [Classification.DME_REQUEST]: "DME request forms, equipment authorization requests, or medical device requisitions",
  [Classification.DME_RESPONSE]:
    "Responses to DME requests, including approvals, denials, or requests for additional information.",
  [Classification.EMERGENCY_SUMMARY]: "Summarizes care provided during an unscheduled emergency or hospital visit.",
  [Classification.FINANCIAL]:
    "General financial documents, billing paperwork, or financial forms not fitting specific financial categories",
  [Classification.FINANCIAL_BILLING_RECORDS]:
    "Billing statements, invoices, charge sheets, or detailed billing records",
  [Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS]:
    "Insurance eligibility verification, benefit determination forms, or coverage eligibility documents. Also called EOB - look for eligible, eligibility. ",
  [Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS_CREDIT_CARD]:
    "Credit card authorization forms for medical services, payment authorization, or financial responsibility forms. Also called EOB",
  [Classification.FINANCIAL_LEGAL]:
    "Legal requests for financial information not specific to a particular patient, including inquiries from attorneys about charges or fees for services rendered.",
  [Classification.IDENTIFICATION_CARD]:
    "Driver's licenses, state IDs, passports, military IDs, or other official identification documents",
  [Classification.IMAGING_RESULTS]:
    "General imaging results, radiology reports, or imaging studies not fitting specific imaging categories",
  [Classification.IMAGING_RESULTS_MULTI]:
    "A single document containing multiple different types of imaging results (e.g., both MRI and CT scans in the same file).",
  [Classification.IMAGING_RESULTS_BARIUM_SWALLOW]:
    "Modified barium swallow (MBS) studies, videofluoroscopic swallow studies, or dynamic swallowing fluoroscopy reports evaluating swallow function",
  [Classification.IMAGING_RESULTS_COLONOSCOPY]:
    "Colonoscopy reports, endoscopic findings, or gastrointestinal imaging results",
  [Classification.IMAGING_RESULTS_CT]: "Computed tomography (CT) scans, CAT scans, or CT imaging reports",
  [Classification.IMAGING_RESULTS_DEXA]:
    "DEXA scan results, bone density studies, or dual-energy X-ray absorptiometry reports",
  [Classification.IMAGING_RESULTS_ECHO]: "Echocardiogram reports, cardiac ultrasound studies, or heart imaging results",
  [Classification.IMAGING_RESULTS_EEG]:
    "(electroencephalogram) imaging is a test that measures electrical activity in the brain using electrodes attached to the scalp",
  [Classification.IMAGING_RESULTS_EMG]:
    "Electromyography results, nerve conduction or muscle electrical studies that look for nerve or muscle disease",
  [Classification.IMAGING_RESULTS_ENG]:
    "Electronystagmography (ENG) or videonystagmography (VNG) studies that evaluate the vestibular system and causes of dizziness or balance disorders via eye movement recording",
  [Classification.IMAGING_RESULTS_ESOPHAGRAM]:
    "Esophagram reports or barium esophagram studies — fluoroscopic imaging of the esophagus to evaluate swallowing, reflux, or structural abnormalities. Distinct from a modified barium swallow, which focuses on the oropharyngeal phase of swallowing",
  [Classification.IMAGING_RESULTS_EKG]:
    "Electrocardiogram results, heart rhythm tracings, or cardiac electrical activity studies",
  [Classification.IMAGING_RESULTS_HOLTER_MONITOR]:
    "Holter monitor results, 24-hour heart monitoring, or extended cardiac rhythm studies",
  [Classification.IMAGING_RESULTS_MAMMOGRAM]:
    "Mammogram reports, breast imaging studies, or breast cancer screening results",
  [Classification.IMAGING_RESULTS_MRI]:
    "Magnetic resonance imaging (MRI) reports, MRI scan results, or magnetic imaging studies",
  [Classification.IMAGING_RESULTS_NM]:
    "Nuclear medicine studies, nuclear imaging reports, or radionuclide imaging results",
  [Classification.IMAGING_RESULTS_OTHER]:
    "Other imaging studies not fitting specific categories, miscellaneous radiology reports",
  [Classification.IMAGING_RESULTS_CARDIAC]:
    "Other cardiac imaging studies, additional heart imaging, or specialized cardiac tests",
  [Classification.IMAGING_RESULTS_PET]:
    "Positron emission tomography (PET) scans, PET imaging reports, or nuclear medicine PET studies",
  [Classification.IMAGING_RESULTS_PULMONARY_FUNCTION]:
    "Pulmonary function tests, breathing studies, or lung function assessments. Pulmonary Function/Spirometry",
  [Classification.IMAGING_RESULTS_RETINAL]:
    "Retinal imaging, eye photography, or ophthalmological imaging studies. If the document looks like a progress note but mentions 'retina' OR 'retinal' OR 'diabetic eye exam', classify as retinal",
  [Classification.IMAGING_RESULTS_STRESS_TEST]:
    "Cardiac stress tests (parent category) covering exercise/treadmill and nuclear-medicine (pharmacologic) stress evaluations",
  [Classification.IMAGING_RESULTS_STRESS_TEST_EXERCISE]:
    "Exercise stress tests, treadmill tests, or cardiac stress evaluations. Exam description will typically include exercise OR treadmill OR treadmill test OR stress ECG OR exercise electrocardiogram",
  [Classification.IMAGING_RESULTS_STRESS_TEST_NM]: "NM Stress Test, for example Bruce Protocol, Lexiscan, Cardiolite",
  [Classification.IMAGING_RESULTS_ULTRASOUND]: "Ultrasound studies, sonography reports, or ultrasound imaging results",
  [Classification.IMAGING_RESULTS_X_RAY]: "X-ray reports, radiographic studies, or plain film imaging results",
  [Classification.IMAGING_RESULTS_NEEDS_CLARIFICATION]: `These are imaging orders (look for diagnostic imaging or imaging types like mammo, x-ray, mri, etc) that are requesting a provider to clarify or adjust something in an imaging order. 
    Look for terms like ICD-10, order codes, billing codes (sometimes these requests have to do with fixing billing coding). 
    These are also requests to change an order or requests for a new order. For example, an initial imaging could return an adverse result, and so a request is made for an order of an additional imaging type. 
    This information is often found in typed or handwritten notes on the cover page or the order page.
    `,
  [Classification.IMAGING_RESULTS_NEEDS_SIGNATURE]: `These are imaging orders (look for diagnostic imaging or imaging types like mammo, x-ray, mri, etc) that are requesting a physician signature or provider signature. 
    This information is often found in typed or handwritten notes on the cover page or the order page.`,
  [Classification.IMAGING_RESULTS_ENDOSCOPY]:
    "Reports summarizing findings from endoscopic procedures (e.g., EGD, colonoscopy, bronchoscopy), including procedure details, findings, impressions, or recommendations",
  [Classification.IMAGING_RESULTS_MONITOR_NOTIFICATION]:
    "System-generated notifications that a transmission to the ACS (Arrhythmia/Cardiac monitoring system) failed or was not received.",
  [Classification.IMAGING_RESULTS_CARDIAC_LEG_IMAGING]:
    "Imaging results of the lower extremities that are Doppler ultrasonography, CT angiography, or magnetic resonance angiography (MRA)",
  [Classification.INCOMPLETE]: "Incomplete documents, partial forms, or documents missing required information",
  [Classification.IMAGING_RESULTS_CLINICAL]:
    "Clinical imaging studies, patient-provided clinical images, or clinical photography",
  [Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT]:
    "Documents that cannot be associated with a specific patient, unidentifiable patient records",
  [Classification.INCOMPLETE_ILLEGIBLE]: `
    Pages where content is present but cannot be reliably read or interpreted: low-resolution or blurry scans, severely degraded handwriting that cannot be deciphered, text physically cropped or cut off at any page edge, fax transmission artifacts (large black bars, heavy smearing, repeated horizontal lines, white-out blocks) that obscure meaningful content, or pages so degraded that the document type cannot be determined.
    Mark any relevant pages as INCOMPLETE_ILLEGIBLE.
    When in doubt, prefer INCOMPLETE_ILLEGIBLE over other classifications.
    `,
  [Classification.INCOMPLETE_NOT_ALL_PAGES]: `
    Documents or faxes where one or more whole pages are missing from what was
    sent. Signals: "Page X of Y" or "X / Y" banners (in headers, footers, or
    corners — often upper-right) where the highest X is less than Y; fax cover
    sheets stating a total page count higher than what was received (e.g.,
    "Number of pages including this cover sheet: N"); pages that end mid-sentence,
    mid-form, mid-table, or mid-list with no continuation; references to a
    signature page, attachment, addendum, or "see next page" with no following
    page; gaps or skips in numbered pagination.

    This classification takes PRECEDENCE over the document's content type:
    whenever a missing-pages or cut-off signal is present, classify the page
    INCOMPLETE_NOT_ALL_PAGES even if the visible content clearly looks like a
    care plan, a therapy / speech / OT / PT evaluation, a progress note, a
    referral, or any other category — a partial or truncated transmission
    outranks whatever the content would otherwise be.

    Treat ANY one of these as sufficient: a fax page banner giving a position
    and total ("Page X of Y", "X / Y", "pg X of Y", commonly in a top or bottom
    margin) whose highest page present is below the stated total Y; a cover sheet
    or header stating a page count ("Pages: N", "Total Pages: N", "including
    this cover sheet: N") greater than the pages actually present; a page whose
    content is cut off at an edge — text ending mid-sentence, mid-word,
    mid-table, or mid-list, a section heading or field label with nothing
    beneath it, a table header whose rows are sheared away, or a page whose
    content runs to the very bottom edge and simply stops; language implying
    more pages ("continued", "see next page", "see attached", "continuation of
    an interrupted transmission", a referenced attachment / addendum / signature
    page that is not present); or gaps in otherwise-sequential page numbering.

    Do not use this classification for a document that is merely short yet
    complete — one that ends at a natural close (a signature block followed by
    blank space, "Return to Office", or a finished assessment / plan) and whose
    page banner, if present, reaches its stated total.

    A fax cover sheet or transmittal page received on its own — with none of the
    content it introduces actually present — is an incomplete transmission: the
    attached evaluation, plan of care, records, or note did not come through.
    Classify such a lone cover sheet INCOMPLETE_NOT_ALL_PAGES rather than as a
    cover page or by the content it merely names.

    Whenever there is ANY doubt about whether pages are missing or a page is cut
    off, always choose INCOMPLETE_NOT_ALL_PAGES over every other classification.
    Mark any relevant pages as INCOMPLETE_NOT_ALL_PAGES.
    `,
  [Classification.INSURANCE_CARD]:
    "Health insurance cards, Medicare cards, Medicaid cards, or insurance identification documents",
  [Classification.INSURANCE_COMMUNICATION]:
    "General insurance correspondence, notifications, eligibility letters, or communications from insurance companies that are not authorization-related.",
  [Classification.INSURANCE_LETTER_OF_ASSURANCE]:
    "Letters of assurance (LOA) from insurance or foster care agencies such as ChildNet, confirming coverage or financial responsibility for a patient's care.",
  [Classification.INTAKE]:
    "Patient registration forms, new patient paperwork, intake questionnaires, or patient demographic information",
  [Classification.INTAKE_DIALYSIS_QUESTIONAIRE]: `
    A form questionnaire that indicates details about a dialysis patient, such as whether they are an isolation patient.
    `,
  [Classification.JUNK]: "Spam, irrelevant content, non-medical documents, or documents that should be discarded",
  [Classification.JUNK_FAILED_FAX]:
    "A fax that a practice attempted to send to an outside recipient, but the transmission failed and the fax was returned undelivered to the practice's own fax inbox.",
  [Classification.LAB_RESULTS]: `
    A laboratory report whose primary purpose is to communicate lab test results. Typically includes test names, result values, reference ranges, and collection/report dates.
    May come from external labs (Quest, LabCorp, BioReference), hospital lab departments, or EHR lab modules.
  `,
  [Classification.LAB_RESULTS_A1C]:
    "Hemoglobin A1C results, diabetes monitoring tests, or long-term glucose control assessments. HbA1c and HbA1c are the same term, referring to glycosylated hemoglobin",
  [Classification.LAB_RESULTS_CBC]:
    "Complete blood count (CBC). A CBC (Complete Blood Count) lab can be with differential (diff) or without differential (diff). These labs typically include values such as RBC Count, Hematocrit, WBC Count, etc.",
  [Classification.LAB_RESULTS_CMP]: `
    This is a Comprehensive Metabolic Panel. These often include labs such as Glucose, Albumin, Total protein, ALP, ALT, AST, BUN and eGFR. They can also be called CMP14
  `,
  [Classification.LAB_RESULTS_MULTIPLE_LABS]: `
    This is a document that contains >1 types of sets of labs, for example CMP and CBC, Hgb1 and Thyroid, Lipid Panel and Microalbumin. This does not mean >1 lab results, it means >1 lab sets.
  `,
  [Classification.LAB_RESULTS_COLOGUARD]:
    "Cologuard test results, colorectal cancer screening tests, or stool-based cancer screening",
  [Classification.LAB_RESULTS_DRUG_SCREEN]: `
    Drug screening results, toxicology reports, or substance abuse testing. 
    Look for terms like Amphetamines (including methamphetamine, MDA, and MDMA), 
    Opiates/Opioids (including heroin, codeine, morphine, hydrocodone, hydromorphone, oxycodone, and oxymorphone), 
    stimulants, Barbiturates, Benzodiazepines, or benzos
  `,
  [Classification.LAB_RESULTS_FIT]: `
    Fecal Immunochemical Test - an at-home colorectal cancer screening tests
  `,
  [Classification.LAB_RESULTS_LIPID_PANEL]:
    "Lipid panel results, cholesterol tests, or cardiovascular risk assessments. A lipid panel typically measures total cholesterol, HDL (high-density lipoprotein) cholesterol, LDL (low-density lipoprotein) cholesterol, and triglycerides",
  [Classification.LAB_RESULTS_MICROALBUMIN]: `
    Microalbumin tests, kidney function assessments, or diabetic kidney disease screening. 
    Microalbumin in urine is typically reported using the albumin-creatinine ratio (ACR). 
    This is also known as the urine albumin-creatinine ratio (UACR). 
    Look out for the term urinalysis, albumin, UAM
  `,
  [Classification.LAB_RESULTS_URINALYSIS]: `
    Urinalysis or kidney-related lab results, including urinalysis panels, urine culture, urine albumin-creatinine ratio (ACR/UACR),
    and other urine-based tests. Use for general urinalysis results that are not specifically microalbumin.
  `,
  [Classification.LAB_RESULTS_ORDER_FORM]:
    "Laboratory order forms, lab requisitions, or test ordering documentation. These are lab requisitions that accompany draw tubes",
  [Classification.LAB_RESULTS_ORDER_FORM_NEEDS_SIGNATURE]: `
    These are lab orders requiring a physician signature or provider signature. 
    This information is often found in typed or handwritten notes on the cover page or the order page.
    `,
  [Classification.LAB_RESULTS_BILIRUBIN]:
    "Bilirubin lab results, including newborn bilirubin screening and jaundice monitoring tests.",
  [Classification.LAB_RESULTS_HEARING_TEST]:
    "Hearing test results, including newborn hearing screenings and audiological assessments.",
  [Classification.LAB_RESULTS_OTHER]: `
    A lab report containing 1 set of labs or one lab type that does not fall into the other specific lab classifications (it is NOT a CMP, CBC, Lipid Panel, Hgb A1c, Cologuard, FIT, Drug screening, or Microalbumin). For example, Testosterone, Thyroid Panel, PSA, etc.
  `,
  [Classification.LAB_RESULTS_PAP_SMEAR]:
    "Pap smear results, cervical cancer screening, or gynecological cytology reports",
  [Classification.LAB_RESULTS_PATHOLOGY]: "Pathology reports, tissue analysis, biopsy results, or histological studies",
  [Classification.LAB_RESULTS_PKU]: "PKU (phenylketonuria) newborn screening test results.",
  [Classification.LAB_RESULTS_PT_INR]: `
    A standalone lab report whose primary and only content is PT/INR (Prothrombin Time/International Normalized Ratio) or anti-coagulation/Coumadin/warfarin monitoring results (e.g., mdINR reports, Acelis Connected Health INR result reports, or standalone Quest/LabCorp PT/INR-only reports).
    It is sometimes listed only as PT (Prothrombin Time) or only as INR (International Normalized Ratio), they are not always combined.
    Do NOT classify as lab_results_pt_inr if:
    - The document is a notification about overdue/missed INR testing, a prescription form, or a DME order for INR monitoring equipment.
  `,
  [Classification.LAB_RESULTS_TB]: `
  TB (or tuberculosis) Test Result Request (most likely will be from dept of public health, or a similar type of organization) 
  `,
  [Classification.LAB_RESULTS_TEST_NOT_PERFORMED]: `
    This is a notification that a lab test was not performed or that a lab order was cancelled. 
    We often see notifications that a cologuard order was cancelled or has expired, that should be within this classification as well
    `,
  [Classification.LAB_RESULTS_HEP]: `
    Hepatitis test results, hepatitis screening, or hepatitis testing. 
    Look for terms like HCV, Hep, Hepatitis, or Hep
  `,
  [Classification.LAB_RESULTS_QUANTAFLO]: `
    Reports or notes related to QuantaFlo diagnostic testing (used for PAD screening)
  `,
  [Classification.LAB_RESULTS_BP]:
    "Logs or spreadsheets of blood pressure readings sent for physician review. May contain date/time, systolic/diastolic, and pulse",
  [Classification.LEGAL]:
    "General legal documents, legal correspondence, or legal forms not fitting specific legal categories",
  [Classification.LEGAL_ADVANCED_CARE_DIRECTIVE]:
    "Advanced directives, living wills, end-of-life care preferences, or healthcare proxy documents",
  [Classification.LEGAL_COURT_DOCUMENTS]:
    "Court orders, custody documents, subpoenas, guardianship papers, or other legal documents issued by or related to court proceedings.",
  [Classification.LEGAL_POWER_OF_ATTORNEY]:
    "Power of attorney documents, healthcare power of attorney, or legal authorization forms",
  [Classification.MEDICAL_HISTORY]: `
    A comprehensive compilation document that aggregates a patient's medical information across multiple encounters and time periods.
    
    KEY IDENTIFYING FEATURES:
    - Spans MULTIPLE dates/encounters (not a single visit)
    - Presents chronological or categorical summary of medical events
    - No single "date of service" defines the document as a whole — but the document still has dated
      encounters, and a representative date should be reported in dateOfEvent (see that field's own
      description for which date to choose)
    - Typically includes: medication lists, immunization records, problem lists, past diagnoses, surgical history
    - Purpose: Provide longitudinal context about the patient's health trajectory
    - Often titled: "Medical History", "Health Summary", "Cumulative Patient Record", "Patient Chart Summary"
    - NOT focused on a single clinical encounter or interaction
    - Does NOT request action from another provider (differentiates from referral)
    
    If the document describes ONE specific encounter with date of service, it's likely a PROGRESS_NOTE.
    If the document requests another provider see the patient, it's likely a REFERRAL.
    `,
  [Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC]: `
    Complete medical chart containing MULTIPLE visits or comprehensive records.

    ON ANY SINGLE PAGE, if you see ANY of these, classify as medical_history_chart_from_clinic:
    - Two or more different visit dates
    - Section titled "Visit History," "Encounter List," "Previous Visits," or "Chart Summary"
    - Table/list showing multiple dated encounters
    - Cover page labeled "Medical Records," "Patient Chart," or "Records Request"
    - Multiple progress notes with different dates

    If ONLY one visit date across ALL pages seen: This is NOT medical_history_chart_from_clinic.
    `,
  [Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL]: `
    These are files that contain patient care documents (most often progress notes, provider notes, encounters, but also imaging, labs, and other document types) that span multiple dates.
    These are specifically from hospitals and will often include ER or ED reports, hospital admit or discharge reports, operative reports, and other hospital-related docs.
    These are most often bulk files with many pages.
    `,
  [Classification.MEDICAL_HISTORY_COVID]:
    "COVID-19 related medical history, coronavirus documentation, or pandemic-related medical records",
  [Classification.MEDICAL_HISTORY_RESPONSE]:
    "Responses to medical record requests indicating a status such as approved, denied, or requesting additional information. Look for ROI response, record release status, denial notices ('records not available', 'patient not found', 'unable to locate records', 'ROI denied'), or incomplete request notices.",
  [Classification.MEDICAL_HISTORY_HOME_HEALTH]: `
    If the document mentions Home Health, or Home Care it should be classified as Home Health.
    `,
  [Classification.MEDICAL_HISTORY_HOSPICE]: `
    If the document mentions hospice OR resident OR nursing home OR memory care OR assisted living facility classify as hospice
    `,
  [Classification.MEDICAL_HISTORY_IMMUNIZATION]: "Immunization records, vaccination history, or vaccine documentation",
  [Classification.MEDICAL_HISTORY_NUTRITION]: `
    These often look like progress notes, but they are for appointments related to nutrition or food or nutritional assessment 
    `,
  [Classification.MEDICAL_HISTORY_MEDICATIONS]: `
    "Medication history, including current medications and former medications.
    `,
  [Classification.MEDICAL_RECORD_REQUEST_IMAGING_CD]:
    "A medical record request specifically for imaging to be burned to CD or disc format. Look for terms like 'CD', 'disc', 'PACS', 'burn imaging', or 'imaging CD'. This should only be used when the request is specifically for imaging results/reports.",
  [Classification.MEDICAL_RECORD_REQUEST]: `
    Look for chart needed, labs needed, imaging needed.
    A document asking for records to be sent. This can include a request for office notes, progress notes, labs, imaging, PET scans, CT Scans, XRays.
    If the request is sent along with progress notes, labs, imaging attached, that is not a medical records request.
  `,
  [Classification.MEDICAL_RECORD_REQUEST_DOH]:
    "Medical record requests from the Department of Health or related public health organizations.",
  [Classification.MEDICAL_RECORD_REQUEST_HEDIS]: `
    HEDIS is the Healthcare Effectiveness Data and Information Set. These are documents that request information about >1 patient.
    These can also be insurance review documents with >1 patient listed. Look for a list of >1 name
  `,
  [Classification.MEDICAL_RECORD_REQUEST_LEGAL]:
    "Medical record requests for legal purposes, including subpoenas, court orders, life insurance requests, or requests citing specific statutes (e.g., 627, 766).",
  [Classification.MEDICAL_RECORD_REQUEST_OTHER]:
    "Medical record requests from external organizations such as specialists, child services, or other healthcare facilities.",
  [Classification.MEDICAL_RECORD_REQUEST_RESPONSE]: `
    Patient records being SENT in response to a prior medical-records request — the records themselves
    (chart notes, progress notes, labs, imaging) bundled and returned, not a request asking for records.
    Often faxed by a third-party records-retrieval service such as Vital Chart or Verisma, or with a
    cover/subject of "Records Request Response". Contrast with MEDICAL_RECORD_REQUEST, which is an
    incoming request asking for records to be sent.
  `,
  [Classification.MEDICAL_RECORD_REQUEST_SSA]:
    "Medical record requests from the Social Security Administration (SSA) or related disability determination services.",
  [Classification.NOTIFICATION]:
    "General notifications, alerts, or communication documents not fitting specific notification categories",
  [Classification.NOTIFICATION_HOSPITAL_ADMIT]:
    "Hospital admission notifications, inpatient admission alerts, or hospital admission documentation",
  [Classification.NOTIFICATION_HOSPITAL_DISCHARGE]:
    "Hospital discharge notifications, discharge summaries, or hospital discharge documentation",
  [Classification.NOTIFICATION_HOSPITAL_ER_ADMIT]:
    "Emergency room admission notifications, ER visit alerts, or emergency department admission records",
  [Classification.NOTIFICATION_HOSPITAL_ER_DISCHARGE]:
    "Emergency room discharge notifications, ER discharge summaries, or emergency department discharge records",
  [Classification.NOTIFICATION_HOSPITAL_FACE_SHEET]:
    "Hospital or ER face sheets containing patient demographic and insurance information collected at registration. Look for terms like face sheet, registration sheet, or patient information sheet.",
  [Classification.NOTIFICATION_APPOINTMENT_CANCELLED_MISSED]: `
    This is a notification that a patient's appointment was cancelled or that a patient missed an appointment. 
    Look for cancelled, missed, no show, etc
    `,
  [Classification.NOTIFICATION_APPOINTMENT]: `
    This is a notification that an appointment has been scheduled. This will clearly be stated as 'patient has been scheduled for an appointment on x date at x time'. 
    If the document says 'we have attempted to contact this patient but have not succeeded', or 'please have the patient call us to schedule this appointment', 
    this is NOT an appointment notification, this should be classified as a referral response. Look for 'upcoming appointment' and look for dates/times of the appointment
    `,
  [Classification.NOTIFICATION_HOSPITAL_SDS_ADMIT]:
    "Hospital SDS (Same day surgery) admission notifications, inpatient admission alerts, or hospital admission documentation",
  [Classification.NOTIFICATION_HOSPITAL_SDS_DISCHARGE]:
    "Hospital SDS (Same day surgery) discharge notifications, inpatient discharge documentation, or hospital stay summaries",
  [Classification.OPERATION_NOTE]: "Summarizes care related to a patient's operation or surgical procedure.",
  [Classification.OTHER]:
    "Documents that don't fit other categories, miscellaneous medical forms, or unclassifiable documents",
  [Classification.ORDER_CARDIAC]: `Orders for cardiac procedures, tests, or treatments. Often sent through EPIC. 
    Look for cardiac catheterization, stress tests, echocardiograms, or other heart-related procedure orders.`,
  [Classification.ORDER_LAB]:
    "Physician requests for laboratory tests (e.g., CBC, lipid panel, A1C, metabolic panel), typically in order-entry format",
  [Classification.ORDER_CLARIFICATION]:
    "Notifications or alerts about invalid or unsupported CPT codes for imaging orders",
  [Classification.ORDER_IMAGING]:
    "Imaging-related order requests pending physician approval, often tagged for review or signature",
  [Classification.ORDER_PROCEDURE]:
    "An order for a surgical procedure, often accompanied by requests for pre-operative labs, imaging, or clearance.",
  [Classification.ORDER_SIGNED]:
    "These documents are imaging or prescription orders that have been signed by the provider. There is no other information regarding clarification or a request for imaging.",
  [Classification.PHARMACY_RELATED]: "General pharmacy documents, medication-related forms, or pharmacy communications",
  [Classification.PHARMACY_RELATED_CARE_CONSIDERATION]: `
    Pharmacy care considerations, medication therapy management, or clinical pharmacy interventions.
    An RX Care Consideration is notifying the provider that a therapy may be beneficial to the patient, this can include a therapeutic device or a prescription.
    It will also often be for notifying the provider about basic prescription updates that require no action, such as a notice that a 90 day supply of medicine was supplied to the patient.
    This also includes shipment notifications and informational pharmacy documents that explicitly state no action is required.`,
  [Classification.PHARMACY_RELATED_CLARIFICATION]: `
    Medication clarifications, prescription clarifications, or pharmacy clarification requests.
    A document from a pharmacy asking the provider to clarify, modify, or make a clinical decision about a prescription.
    This includes medical clarification requests, prescription change order requests, drug utilization reviews, drug therapy requests,
    and any document where the pharmacy asks a question or flags a concern about a prescription.
    If the pharmacy is asking the provider to address a problem, answer a question, or choose between options, it is a clarification.
`,
  [Classification.PHARMACY_RELATED_CONTROLED_SUBSTANCES_POLICY]:
    "Controlled substance policies, narcotic prescribing guidelines, or controlled medication documentation",
  [Classification.PHARMACY_RELATED_DRUG_MONITORING_PROGRAM]:
    "Drug monitoring program documentation, medication monitoring, or therapeutic drug monitoring. This means Rx Prescription Drug Monitoring Program (PDMP) Documents",
  [Classification.PHARMACY_RELATED_MED_RECONCILIATION]:
    "Medication reconciliation documents, typically created post-discharge, listing current medications and any changes made during a hospital stay or care transition.",
  [Classification.PHARMACY_RELATED_MEDICATION_LIST]:
    "Personal medication lists, current medication summaries, or medication inventories provided by or on behalf of the patient.",
  [Classification.PHARMACY_RELATED_NON_ADHERENCE]:
    "Medication non-adherence reports, missed medication documentation, or compliance issues",
  [Classification.PHARMACY_RELATED_REFILL]: `
    A standardized pharmacy form explicitly asking the prescriber for permission to dispense medication - a refill or new prescription.
    The only action needed is for the provider to approve the prescription - no additional information or decisions required.
    The document should be clearly titled as a request, such as "Refill Authorization Request", "Refill Request",
    "RxRenewal Request", "NEW PRESCRIPTION REQUEST", "90 DAY PRESCRIPTION REQUEST", or "PATIENT REQUEST FOR NEW PRESCRIPTION".
    A prescription form, medication order, "New Rx", "New Prescription", "Renewal Response", or "Prior Physician Written Order" showing drug details
    is a completed prescription or order - not a refill request. A refill request asks for authorization, not shows what was already prescribed.
    A "Prior Refill Authorization Request" is a prior authorization, not a refill.
    An adherence communication, "late to fill" notification, or information request form is not a refill even if it mentions medications.
    If the document requests additional information, asks a question, flags a drug concern, or requests clarification - it is not a refill.
    A document that leads with a clarification or medical information request is NOT a refill, even if it includes prescription or renewal pages.
    The document must be a complete, standalone pharmacy request - not a page within a larger medical record or a truncated/incomplete document.
    Chart pages documenting refill encounters or medication orders within a patient record are part of the medical history, not refill requests.`,
  [Classification.PHARMACY_RELATED_REFILL_MANY]:
    "Multiple medication refills, bulk refill requests, or comprehensive medication renewals",
  [Classification.PHARMACY_RELATED_ALERT]: "Therapeutic alerts, medication warnings, or clinical pharmacy alerts",
  [Classification.PRIOR_AUTHORIZATION]:
    "General prior authorization forms, pre-approval requests, or authorization documentation",
  [Classification.PRIOR_AUTHORIZATION_IMAGING_REQUEST]:
    "A prior authorization request specifically for imaging studies such as MRI, CT, X-ray, ultrasound, or other diagnostic imaging.",
  [Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE]:
    "An approval or denial response from an insurance company for an imaging prior authorization request.",
  [Classification.PRIOR_AUTHORIZATION_SURGERY_REQUEST]: "A prior authorization request for a surgical procedure.",
  [Classification.PRIOR_AUTHORIZATION_SURGERY_RESPONSE]:
    "An approval or denial response from an insurance company for a surgical prior authorization request.",
  [Classification.PRIOR_AUTHORIZATION_THERAPY_REQUEST]:
    "A prior authorization request for physical therapy, occupational therapy, or other rehabilitation therapy services.",
  [Classification.PRIOR_AUTHORIZATION_THERAPY_RESPONSE]:
    "An approval or denial response from an insurance company for a therapy prior authorization request.",
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST]: `Medication prior authorization requests, drug approval requests, or prescription authorization forms For prior auth medication request vs response, 
    if the document is from a pharmacy and the pharmacy has denied to refill the prescription and require a prior authorization, this is a prior auth request, since a prior auth will be needed for the pharmacy.
    This is a request for a prior authorization for a medication to be initiated or completed. This includes notifications that a prior authorization is pending, or requests for additional information about a prior authorization. 
    `,
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: `Medication authorization responses, drug approval decisions, or prescription authorization results For prior auth medication request vs response, 
    if the document is from a pharmacy and the pharmacy has denied to refill the prescription and require a prior authorization, this is a prior auth request, since a prior auth will be needed for the pharmacy.
    This is the approval or denial of a prior authorization request for a medication.
    `,
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST]: `
    This is a request for a prior authorization for procedure OR a referral OR a consult OR DME (durable medical equipment) to be initiated or completed. 
    This includes notifications that a prior authorization is pending, or requests for additional information about a prior authorization.
    `,
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: `
    This is the approval or denial of a prior authorization request for a procedure OR a referral OR a consult OR DME (durable medical equipment).
    `,
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_REQUEST]:
    "A request for a prior authorization for a referral to be initiated or completed. This includes notifications that a referral authorization is pending, or requests for additional information about a referral authorization.",
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_RESPONSE]:
    "Approval or denial of a referral authorization from an insurance company. Look for terms like referral authorization, referral approved, referral denied, or authorization number associated with a specialist referral.",
  [Classification.PROCEDURE]:
    "General procedure documentation, surgical procedures, or medical procedures not fitting specific categories",
  [Classification.PROCEDURE_CARDIAC]: `
    Cardiac procedures encompass a wide range of treatments for heart conditions, including surgery, interventions, and monitoring. 
    Some common procedures include coronary artery bypass grafting (CABG), angioplasty and stenting, valve repair or replacement, pacemaker implantation, and defibrillator implantation.
  `,
  [Classification.PROCEDURE_DEVICE_REQUEST]: `
    Forms or requests to transfer device or implantable data (e.g., pacemaker, ICD, loop recorder) between systems or facilities.
  `,
  [Classification.PROCEDURE_DEVICE_INFO]: `
    Implant registration cards, device info sheets, or scanned manufacturer implant cards with timestamps for review.
  `,
  [Classification.PROCEDURE_GI]: `
    Gastrointestinal procedures, digestive system interventions, or GI surgery. 
    EGD stands for Esophagogastroduodenoscopy, also known as an upper endoscopy, it is an exam of the upper gastrointestinal tract
  `,
  [Classification.PROCEDURE_SLEEP]: "Sleep studies, sleep procedures, or sleep-related interventions",
  [Classification.PROCEDURE_CARDIAC_VEIN]:
    "Patient forms or surveys assessing venous conditions, such as leg swelling, varicose veins, pain, or mobility",
  [Classification.PROCEDURE_INVENTORY_LIST]: `
    Inventory lists, equipment lists, or supply lists for procedures or surgeries.
  `,
  [Classification.PROCEDURE_REHAB]:
    "Documents related to cardiac rehabilitation programs, exercise plans, progress notes, or assessments of cardiovascular recovery",
  [Classification.PROGRESS_NOTE]: `
    A document that chronicles a SINGLE, SPECIFIC clinical encounter between a patient and provider.

    KEY IDENTIFYING FEATURES:
    - Associated with ONE specific encounter date ("Date of Service", "Date of Visit", "Encounter Date")
    - Documents what happened during THIS particular visit
    - Often follows SOAP format (Subjective, Objective, Assessment, Plan) but not always
    - Typically signed/authenticated by the provider who conducted the encounter
    - Includes encounter-specific details: chief complaint, reason for visit, vital signs from that day
    - Purpose: Document and track patient's status at a point in time; communicate with other providers about this encounter
    - Commonly titled: "Progress Note", "Visit Note", "Clinical Note", "SOAP Note", "Encounter Note"
    - Contains demographic information (name, DOB, address, phone, insurance)

    If the document summarizes multiple encounters across different dates, it's likely MEDICAL_HISTORY.
    If the document's primary purpose is requesting another provider see the patient, it's likely a REFERRAL.
    `,
  [Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY]: `
    In classifying, be sure to check if this document is from a hospital. If the document mentions home health, it should be classified as Home Health, even if the document says discharge summary.
  `,
  [Classification.PROGRESS_NOTE_ER_REPORT]: `
    These are often 'provider notes'
  `,
  [Classification.PROGRESS_NOTE_ER_SUMMARY]: `
    This document should most often specifically say "Summary of Care". If it says "discharge summary", it should be classified as Discharge Summary. It will say ER or ED or Emergency Room or Emergency Department
  `,
  [Classification.PROGRESS_NOTE_HOSPITAL_OFFICE_VISIT]:
    "Hospital office visit notes, inpatient consultations, or hospital-based outpatient visits",
  [Classification.PROGRESS_NOTE_HOSPITAL_RECORDS]: `
    "H&P" most commonly refers to the History and Physical, a crucial medical documentation process. It's a detailed account of a patient's medical history and a physical examination performed by a healthcare professional.
  `,
  [Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY]: `
    This document should most often specifically say "Summary of Care". If it says "discharge summary", it should be classified as Discharge Summary
  `,
  [Classification.PROGRESS_NOTE_OFFICE_VISIT]: `
    Single encounter note from ONE date at a specialty clinic.
    
    ON EVERY PAGE, check for these disqualifying signals FIRST:
    - Does this page show a DIFFERENT visit date than previous pages?
    - Does this page have headers like "Visit History," "Previous Encounters," "Chart Summary"?
    - Does this page show a list of multiple dated visits?
    
    If ANY disqualifying signal found: This is NOT progress_note_office_visit.
    
    If NO disqualifying signals, check for single-visit indicators:
    - ONE consistent visit date (can repeat across pages)
    - Content flows as one narrative (chief complaint → exam → assessment → plan)
    - Headers like "Office Visit," "Progress Note," "SOAP Note," "Consultation" with single date
    
    If uncertain after seeing multiple pages: This is likely NOT progress_note_office_visit.
  `,
  [Classification.PROGRESS_NOTE_OPERATIVE_REPORT]:
    "Operative reports, surgery documentation, or surgical procedure notes",
  [Classification.PROGRESS_NOTE_PRE_OPERATIVE]:
    "A note containing pre-operative reports — typically a bundle including labs, imaging, and clinical evaluations gathered ahead of a planned surgery.",
  [Classification.PROGRESS_NOTE_PT]: `
    This means Physical Therapy, Occupational Therapy, and Speech Therapy. 
    If a document is focused on one of these categories, classify as PT/OT/ST. 
    This also includes outpatient therapy documents.
  `,
  [Classification.PROGRESS_NOTE_URGENT_CARE]:
    "Urgent care notes, walk-in clinic documentation, or immediate care visit records",
  [Classification.PROGRESS_NOTE_VITAL_CHART]:
    "A vital signs log or report — a record of a patient's measured vitals (blood pressure, heart rate, temperature, weight, oxygen saturation) captured over one or more encounters.",
  [Classification.PROGRESS_NOTE_FLOW_SHEET]: `
    A flow sheet is a structured clinical document that systematically tracks a patient's vital signs, treatment parameters, medications, and clinical status over time. These documents are commonly used in dialysis units, respiratory therapy, intensive care units, and other settings requiring frequent monitoring. Flow sheets typically include:
    - Vital signs (blood pressure, heart rate, temperature, oxygen saturation)
    - Treatment parameters (dialysis settings, ventilator settings, medication dosages)
    - Clinical assessments (level of consciousness, pain scores, functional status)
    - Time-stamped entries for each measurement or intervention
    - Provider signatures and documentation of care provided
    - Treatment outcomes and patient response to interventions
    
    Flow sheets are designed for rapid documentation and quick reference during patient care, often using checkboxes, grids, or standardized forms to ensure consistent data collection.
  `,
  [Classification.REFERRAL]: `
    A document related to referring a patient from one provider/facility to another.

    CLASSIFY AS REFERRAL IF ANY OF THE FOLLOWING ARE TRUE:
    - The word "referral", "refer", "referred", or "referring" appears anywhere on the page
    - Titled or stamped: "Referral", "Referral Order", "Referral Request", "Consultation Request", "Consult Request"
    - Contains a "Reason for Referral" or "Referral Indication" section
    - Identifies both a referring provider/location AND a receiving provider/specialty/location
    - Requests an appointment, consultation, evaluation, or treatment from another provider
    - Contains a referral or authorization number
    - Contains a request or order for a new office visit or follow-up appointment — even if the page otherwise resembles a prior authorization, progress note, lab result, or other document type

    When in doubt, prefer REFERRAL over other classifications.
  `,
  [Classification.REFERRAL_OUTSIDE]: "External referrals, out-of-network referrals, or referrals to outside providers",
  [Classification.REFERRAL_REQUEST]: `
  This is a request asking for a referral document to be sent or updated
  `,
  [Classification.CONSENT_FOR_RECORDS_SPECIALIST]: `
    A consent-for-records form sent from a specialist's office (or from the patient via the specialist) authorizing the release of the patient's records to the specialist who is fulfilling a referral.

    Typically a signed HIPAA / authorization-to-release-records form, often accompanied by a fax cover sheet from the specialist requesting records for an upcoming appointment.
  `,
  [Classification.REFERRAL_RESPONSE]: `
    A referral response is a message from a provider to whom the patient was referred.
    The message will often be requesting more information, notifying the practice that they have scheduled the patient, or cannot see or schedule the patient for some reason, or saying that the patient declined/canceled.
    This will often come along with the original referral that was sent to the provider.
    If a progress note is accompanying the referral response, the classification changes to Progress Note.
  `,
  [Classification.REFERER_INFORMATION]:
    "Information about referring providers, referring physician details, or referral source documentation",
  [Classification.REFERRAL_STATUS_REQUEST]:
    "Referral status inquiries, follow-up on referrals, or referral tracking requests",
  [Classification.RESPONSE_TO_VH_REQUEST]:
    "Responses to Valerie Health requests, VH inquiries, or responses to VH communications",
  [Classification.UNKNOWN]:
    "Documents that cannot be clearly classified, unclear content, or documents requiring human review for classification",
  [Classification.VITAL_CHART]:
    "Any document sent by the VitalChart (Vital Chart) records-retrieval service, identified by its cover sheet or branding, regardless of the underlying content type.",
};

export const DEFAULT_CLASSIFICATIONS_TO_DISPLAY = [
  Classification.ADMIN_LEAD_FORM,
  Classification.BLANK,
  Classification.CARE_PLAN,
  Classification.CONSENT,
  Classification.COVER_PAGE,
  Classification.DME_ORDER,
  Classification.IDENTIFICATION_CARD,
  Classification.IMAGING_RESULTS,
  Classification.INSURANCE_CARD,
  Classification.INTAKE,
  Classification.FINANCIAL,
  Classification.JUNK,
  Classification.LAB_RESULTS,
  Classification.LEGAL,
  Classification.MEDICAL_HISTORY,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.OTHER,
  Classification.PHARMACY_RELATED,
  Classification.PRIOR_AUTHORIZATION,
  Classification.PROCEDURE_SLEEP,
  Classification.PROCEDURE_INVENTORY_LIST,
  Classification.PROGRESS_NOTE,
  Classification.REFERRAL,
  Classification.REFERRAL_STATUS_REQUEST,
  Classification.REFERER_INFORMATION,
  Classification.RESPONSE_TO_VH_REQUEST,
  Classification.UNKNOWN,
  Classification.PHARMACY_RELATED_REFILL,
];

// Classification Groups
export const REFERRAL_CLASSIFICATIONS = [
  Classification.REFERRAL,
  Classification.REFERER_INFORMATION,
  Classification.RESPONSE_TO_VH_REQUEST,
];

export const IDENTIFICATION_CLASSIFICATIONS = [Classification.IDENTIFICATION_CARD, Classification.ADMIN_LEAD_FORM];

export const LAB_RESULTS_CLASSIFICATIONS = [
  Classification.LAB_RESULTS,
  Classification.LAB_RESULTS_A1C,
  Classification.LAB_RESULTS_BILIRUBIN,
  Classification.LAB_RESULTS_BP,
  Classification.LAB_RESULTS_CBC,
  Classification.LAB_RESULTS_CMP,
  Classification.LAB_RESULTS_COLOGUARD,
  Classification.LAB_RESULTS_DRUG_SCREEN,
  Classification.LAB_RESULTS_FIT,
  Classification.LAB_RESULTS_HEARING_TEST,
  Classification.LAB_RESULTS_HEP,
  Classification.LAB_RESULTS_LIPID_PANEL,
  Classification.LAB_RESULTS_MICROALBUMIN,
  Classification.LAB_RESULTS_MULTIPLE_LABS,
  Classification.LAB_RESULTS_ORDER_FORM,
  Classification.LAB_RESULTS_ORDER_FORM_NEEDS_SIGNATURE,
  Classification.LAB_RESULTS_OTHER,
  Classification.LAB_RESULTS_PAP_SMEAR,
  Classification.LAB_RESULTS_PATHOLOGY,
  Classification.LAB_RESULTS_PKU,
  Classification.LAB_RESULTS_PT_INR,
  Classification.LAB_RESULTS_QUANTAFLO,
  Classification.LAB_RESULTS_TB,
  Classification.LAB_RESULTS_TEST_NOT_PERFORMED,
  Classification.LAB_RESULTS_URINALYSIS,
];

export const PROGRESS_NOTE_CLASSIFICATIONS = [
  Classification.CONTINUITY_OF_CARE,
  Classification.PROGRESS_NOTE_FLOW_SHEET,
  Classification.PROGRESS_NOTE,
  Classification.PROGRESS_NOTE_ER_REPORT,
  Classification.PROGRESS_NOTE_ER_SUMMARY,
  Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
  Classification.PROGRESS_NOTE_HOSPITAL_OFFICE_VISIT,
  Classification.PROGRESS_NOTE_HOSPITAL_RECORDS,
  Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY,
  Classification.PROGRESS_NOTE_OFFICE_VISIT,
  Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
  Classification.PROGRESS_NOTE_PRE_OPERATIVE,
  Classification.PROGRESS_NOTE_PT,
  Classification.PROGRESS_NOTE_URGENT_CARE,
  Classification.PROGRESS_NOTE_VITAL_CHART,
];

export const IMAGING_RESULTS_CLASSIFICATIONS = [
  Classification.IMAGING_RESULTS,
  Classification.IMAGING_RESULTS_MULTI,
  Classification.IMAGING_RESULTS_BARIUM_SWALLOW,
  Classification.IMAGING_RESULTS_CLINICAL,
  Classification.IMAGING_RESULTS_COLONOSCOPY,
  Classification.IMAGING_RESULTS_CT,
  Classification.IMAGING_RESULTS_DEXA,
  Classification.IMAGING_RESULTS_ECHO,
  Classification.IMAGING_RESULTS_EEG,
  Classification.IMAGING_RESULTS_EKG,
  Classification.IMAGING_RESULTS_EMG,
  Classification.IMAGING_RESULTS_ENG,
  Classification.IMAGING_RESULTS_ESOPHAGRAM,
  Classification.IMAGING_RESULTS_HOLTER_MONITOR,
  Classification.IMAGING_RESULTS_MAMMOGRAM,
  Classification.IMAGING_RESULTS_MRI,
  Classification.IMAGING_RESULTS_NM,
  Classification.IMAGING_RESULTS_OTHER,
  Classification.IMAGING_RESULTS_CARDIAC,
  Classification.IMAGING_RESULTS_PET,
  Classification.IMAGING_RESULTS_PULMONARY_FUNCTION,
  Classification.IMAGING_RESULTS_RETINAL,
  Classification.IMAGING_RESULTS_STRESS_TEST_EXERCISE,
  Classification.IMAGING_RESULTS_STRESS_TEST_NM,
  Classification.IMAGING_RESULTS_ULTRASOUND,
  Classification.IMAGING_RESULTS_X_RAY,
  Classification.IMAGING_RESULTS_NEEDS_CLARIFICATION,
  Classification.IMAGING_RESULTS_NEEDS_SIGNATURE,
  Classification.IMAGING_RESULTS_ENDOSCOPY,
  Classification.IMAGING_RESULTS_MONITOR_NOTIFICATION,
  Classification.IMAGING_RESULTS_CARDIAC_LEG_IMAGING,
];

export const PRIOR_AUTHORIZATION_CLASSIFICATIONS = [
  Classification.PRIOR_AUTHORIZATION,
  Classification.PRIOR_AUTHORIZATION_IMAGING_REQUEST,
  Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
  Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
  Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_REFERRAL_REQUEST,
  Classification.PRIOR_AUTHORIZATION_REFERRAL_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_SURGERY_REQUEST,
  Classification.PRIOR_AUTHORIZATION_SURGERY_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_THERAPY_REQUEST,
  Classification.PRIOR_AUTHORIZATION_THERAPY_RESPONSE,
];

export const PHARMACY_RELATED_CLASSIFICATIONS = [
  Classification.PHARMACY_RELATED,
  Classification.PHARMACY_RELATED_CARE_CONSIDERATION,
  Classification.PHARMACY_RELATED_CLARIFICATION,
  Classification.PHARMACY_RELATED_CONTROLED_SUBSTANCES_POLICY,
  Classification.PHARMACY_RELATED_DRUG_MONITORING_PROGRAM,
  Classification.PHARMACY_RELATED_MED_RECONCILIATION,
  Classification.PHARMACY_RELATED_MEDICATION_LIST,
  Classification.PHARMACY_RELATED_NON_ADHERENCE,
  Classification.PHARMACY_RELATED_REFILL,
  Classification.PHARMACY_RELATED_REFILL_MANY,
  Classification.PHARMACY_RELATED_ALERT,
];

export const DME_CLASSIFICATIONS = [
  Classification.DME_ORDER,
  Classification.DME_DIABETIC_SUPPLIES,
  Classification.DME_REQUEST,
  Classification.DME_RESPONSE,
];

export const MEDICAL_RECORD_REQUEST_CLASSIFICATIONS = [
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.MEDICAL_RECORD_REQUEST_DOH,
  Classification.MEDICAL_RECORD_REQUEST_IMAGING_CD,
  Classification.MEDICAL_RECORD_REQUEST_HEDIS,
  Classification.MEDICAL_RECORD_REQUEST_LEGAL,
  Classification.MEDICAL_RECORD_REQUEST_OTHER,
  Classification.MEDICAL_RECORD_REQUEST_RESPONSE,
  Classification.MEDICAL_RECORD_REQUEST_SSA,
];

export const LEGAL_CLASSIFICATIONS = [
  Classification.LEGAL,
  Classification.LEGAL_ADVANCED_CARE_DIRECTIVE,
  Classification.LEGAL_COURT_DOCUMENTS,
  Classification.LEGAL_POWER_OF_ATTORNEY,
];

export const INTAKE_CLASSIFICATIONS = [Classification.INTAKE, Classification.INTAKE_DIALYSIS_QUESTIONAIRE];

export const INSURANCE_CARD_CLASSIFICATIONS = [
  Classification.INSURANCE_CARD,
  Classification.INSURANCE_COMMUNICATION,
  Classification.INSURANCE_LETTER_OF_ASSURANCE,
];

export const FINANCIAL_CLASSIFICATIONS = [
  Classification.FINANCIAL,
  Classification.FINANCIAL_BILLING_RECORDS,
  Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS,
  Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS_CREDIT_CARD,
  Classification.FINANCIAL_LEGAL,
];

export const CARE_PLAN_CLASSIFICATIONS = [Classification.CARE_PLAN, Classification.CARE_PLAN_REMINDER];

export const MEDICAL_HISTORY_CLASSIFICATIONS = [
  Classification.MEDICAL_HISTORY,
  Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC,
  Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL,
  Classification.MEDICAL_HISTORY_COVID,
  Classification.MEDICAL_HISTORY_HOME_HEALTH,
  Classification.MEDICAL_HISTORY_HOSPICE,
  Classification.MEDICAL_HISTORY_IMMUNIZATION,
  Classification.MEDICAL_HISTORY_MEDICATIONS,
  Classification.MEDICAL_HISTORY_NUTRITION,
  Classification.MEDICAL_HISTORY_RESPONSE,
];

// Helper functions to check if a classification belongs to a group
export function isLabResultsClassification(classification: Classification): boolean {
  return LAB_RESULTS_CLASSIFICATIONS.includes(classification);
}

export function isProgressNoteClassification(classification: Classification): boolean {
  return PROGRESS_NOTE_CLASSIFICATIONS.includes(classification);
}

export function isImagingResultsClassification(classification: Classification): boolean {
  return IMAGING_RESULTS_CLASSIFICATIONS.includes(classification);
}

export function isMedicalHistoryClassification(classification: Classification): boolean {
  return MEDICAL_HISTORY_CLASSIFICATIONS.includes(classification);
}

// more broadly captures any classification that contains medical information
export function isMedicalInfoClassification(classification: Classification): boolean {
  return (
    isProgressNoteClassification(classification) ||
    isLabResultsClassification(classification) ||
    isImagingResultsClassification(classification) ||
    isMedicalHistoryClassification(classification)
  );
}
